import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Pencil, X, PackageSearch, AlertTriangle, FileDown, FileText, ScanSearch } from "lucide-react";
import { brl, fdate } from "@/pages/portal/Dashboard";
import { JAT_CHARACTERISTICS, jatCharLabel } from "@/lib/jateamento";

const SVC_LABELS = { plasma: "Corte Plasma", laser: "Corte Laser", dobra: "Dobra", jateamento: "Jateamento", gravacao: "Gravação" };
const JAT_DIFF_LABELS = { baixa: "Baixa", media: "Média", alta: "Alta", muito_alta: "Muito alta" };
const JAT_FACES = Array.from({ length: 10 }, (_, i) => i + 1);
const IMG_EXTS = ["jpg", "jpeg", "png"];

const inputCls =
  "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
const labelCls = "text-xs uppercase tracking-wider text-slate-400";

const CALC_ROWS = [
  ["material_cost", "Material", brl],
  ["plasma_cost", "Corte Plasma", brl],
  ["laser_cost", "Corte Laser", brl],
  ["dobra_cost", "Dobra", brl],
  ["discount", "Desconto", (v) => `- ${brl(v)}`],
  ["surcharge", "Acréscimo", brl],
];

// Composição detalhada do item (revisão do orçamento): cada componente com sua
// unidade de cobrança; setup cobrado uma única vez por item/serviço.
const compositionText = (calc) => {
  const cp = calc?.composition;
  if (!cp) return "";
  const parts = [];
  if (cp.material) {
    parts.push(`Material: área ${cp.material.area_piece_m2} m² × (1 + ${cp.material.material_loss_pct}% perda) = ${cp.material.area_considered_m2} m² × ${brl(cp.material.price_per_m2)}/m² × ${cp.quantity} un = ${brl(cp.material.total)}`);
  }
  for (const s of ["plasma", "laser"]) {
    if (cp[s]) {
      parts.push(`${SVC_LABELS[s]}: ${cp[s].cut_m_per_piece} m/peça × ${brl(cp[s].price_per_meter)}/m + ${cp[s].pierces_per_piece} entradas/peça × ${brl(cp[s].price_per_pierce)}, × ${cp.quantity} un, setup 1× ${brl(cp[s].setup_charged_once)} = ${brl(cp[s].total)}`);
    }
  }
  if (cp.dobra) {
    parts.push(`Dobra: ${cp.dobra.bend_m_per_piece} m/peça × ${brl(cp.dobra.price_per_meter)}/m × ${cp.quantity} un = ${brl(cp.dobra.total)}`);
  }
  return parts.join("  •  ");
};
const METRIC_FIELDS = [
  ["sheet_area_m2", "Área da peça na chapa (m²)"],
  ["cut_meters", "Metros de corte (m)"],
  ["pierces", "Furos/entradas"],
  ["bend_meters", "Metros de dobra (m)"],
];
const OVERRIDE_FIELDS = [
  ["price_per_m2", "R$/m² material"],
  ["material_loss_pct", "Perda material (%)"],
  ["plasma_price_per_meter", "Plasma R$/m"],
  ["plasma_price_per_pierce", "Plasma R$/furo"],
  ["plasma_setup", "Plasma setup (R$)"],
  ["laser_price_per_meter", "Laser R$/m"],
  ["laser_price_per_pierce", "Laser R$/furo"],
  ["laser_setup", "Laser setup (R$)"],
  ["dobra_price_per_meter", "Dobra R$/m"],
  ["discount", "Desconto (R$)"],
  ["surcharge", "Acréscimo (R$)"],
  ["total", "Total do item forçado (R$)"],
];

const JAT_CALC_ROWS = [
  ["area_per_piece_m2", "Área por peça", (v) => `${v} m²`],
  ["area_total_m2", "Área total estimada", (v) => `${v} m²`],
  ["faces", "Faces", (v) => `${v}`],
  ["price_per_m2", "Valor por m²", brl],
  ["base_value", "Valor base", brl],
  ["difficulty_pct", "Acréscimo", (v) => `${v}%`],
  ["difficulty_value", "Valor do acréscimo", brl],
  ["minimum_applied", "Valor mínimo aplicado", (v) => (v ? "Sim" : "Não")],
  ["extras", "Adicionais", brl],
];
const JAT_OVERRIDE_FIELDS = [
  ["area_m2", "Área manual por peça (m²)"],
  ["price_per_m2", "Valor/m² manual (R$)"],
  ["extras", "Adicionais (R$)"],
  ["total", "Total do item forçado (R$)"],
];
const JAT_EMPTY = { length_mm: "", width_mm: "", height_mm: "", weight_kg: "", quantity: "1", faces: "", difficulty: "",
                    piece_type: "", part_material: "", part_grade: "", part_thickness_mm: "",
                    characteristics: [], characteristics_other: "", notes: "", photos: [] };
const DOBRA_CALC_ROWS = [
  ["meters_per_piece", "Metros por peça", (v) => `${v} m`],
  ["total_meters", "Metros lineares", (v) => `${v} m`],
  ["price_per_meter", "Valor por metro", brl],
  ["calculated_value", "Valor calculado", brl],
  ["minimum_value", "Valor mínimo", brl],
  ["minimum_applied", "Mínimo aplicado", (v) => (v ? "Sim" : "Não")],
];
const DOBRA_OVERRIDE_FIELDS = [
  ["price_per_meter", "Dobra R$/m manual"],
  ["minimum_value", "Valor mínimo (R$)"],
  ["total", "Total do item forçado (R$)"],
];
const DOBRA_EMPTY = { material: "", thickness_mm: "", bend_length_mm: "", bend_count: "" };
const GRAV_CALC_ROWS = [
  ["area_cm2", "Área", (v) => `${v} cm²`],
  ["price_per_cm2", "Valor por cm²", brl],
  ["calculated_value", "Valor calculado", brl],
  ["minimum_value", "Valor mínimo", brl],
  ["minimum_applied", "Mínimo aplicado", (v) => (v ? "Sim" : "Não")],
  ["setup_charged_once", "Setup (1×)", brl],
];
const GRAV_OVERRIDE_FIELDS = [
  ["price_per_cm2", "Gravação R$/cm² manual"],
  ["minimum_value", "Valor mínimo (R$)"],
  ["setup", "Setup (R$)"],
  ["total", "Total do item forçado (R$)"],
];
const GRAV_EMPTY = { machine: "", material: "", depth: "", width_mm: "", height_mm: "", quantity: "1", notes: "" };
const USI_SERVICES = ["Centro de Usinagem CNC", "Torno CNC", "Fresamento", "Tornearia"];
const USI_FMT = [["cilindrica", "Cilíndrica"], ["quadrada", "Quadrada"], ["retangular", "Retangular"]];
const USI_EMPTY = { service_name: "Centro de Usinagem CNC", material: "", format: "", dim_a: "", dim_b: "", dim_c: "", tolerance: "" };
const usiDimsText = (u) => {
  if (u.format === "cilindrica") return `Ø ${u.dim_a} × ${u.dim_b} mm`;
  if (u.format === "quadrada") return `${u.dim_a} × ${u.dim_a} × ${u.dim_b} mm`;
  if (u.format === "retangular") return `${u.dim_a} × ${u.dim_b} × ${u.dim_c} mm`;
  return "—";
};

function Thumb({ quoteId, fileIndex }) {
  const [src, setSrc] = useState(null);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/quote-requests/${quoteId}/files/${fileIndex}/thumbnail`, { responseType: "blob" })
      .then(({ data }) => { if (alive) { setSrc(URL.createObjectURL(data)); setTried(true); } })
      .catch(() => alive && setTried(true));
    return () => { alive = false; };
  }, [quoteId, fileIndex]);
  if (!tried) return <div className="w-full h-full grid place-content-center"><Loader2 className="w-4 h-4 animate-spin text-slate-700" /></div>;
  if (!src) return <PackageSearch className="w-6 h-6 text-slate-700" />;
  return <img src={src} alt="Geometria DXF" className="max-w-full max-h-full object-contain" />;
}

function Photo({ quoteId, idx, name, size = "w-16 h-16" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/quote-requests/${quoteId}/files/${idx}`, { responseType: "blob" })
      .then(({ data }) => { if (alive) setSrc(URL.createObjectURL(data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [quoteId, idx]);
  if (!src) {
    return <div className={`${size} rounded-sm border border-slate-800 bg-slate-900/60 grid place-content-center`}><Loader2 className="w-4 h-4 animate-spin text-slate-700" /></div>;
  }
  return <img src={src} alt={name || `Foto ${idx + 1}`} className={`${size} object-cover rounded-sm border border-slate-800`} data-testid={`jat-photo-${idx}`} />;
}

// Campos de VALORES editáveis inline por tipo de item — taxas e valores absolutos
// por componente (vazio = valor calculado pelo motor; alterar recalcula o item).
const valueFieldsFor = (it) => {
  const t = it.item_type || "chapa";
  if (t === "jateamento") return [["price_per_m2", "Valor por m² (R$)"], ["extras", "Adicionais (R$)"], ["total", "Total do item (R$)"]];
  if (t === "dobra") return [["price_per_meter", "Valor por metro (R$)"], ["minimum_value", "Valor mínimo (R$)"], ["total", "Total do item (R$)"]];
  if (t === "gravacao") return [["price_per_cm2", "Valor por cm² (R$)"], ["setup", "Setup (R$)"], ["minimum_value", "Valor mínimo (R$)"], ["total", "Total do item (R$)"]];
  if (t === "usinagem") return [["total", "Total do item (R$)"]];
  return [
    ["material_cost", "Valor do material (R$)"],
    ...(it.services?.plasma ? [["plasma_cost", "Valor do corte plasma (R$)"]] : []),
    ...(it.services?.laser ? [["laser_cost", "Valor do corte laser (R$)"]] : []),
    ...(it.services?.dobra ? [["dobra_cost", "Valor da dobra (R$)"]] : []),
    ["surcharge", "Outros custos (R$)"],
    ["discount", "Desconto (R$)"],
    ["unit_price", "Valor unitário (R$)"],
    ["total", "Total do item (R$)"],
  ];
};

// Edição inline dos valores do item (overrides) — salva e recalcula no servidor.
function ItemValues({ it, fields, onSave, saving }) {
  const [vals, setVals] = useState(() => Object.fromEntries(fields.map(([k]) => [k, it.overrides?.[k] ?? ""])));
  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
  return (
    <div className="mt-1.5" data-testid={`item-values-${it.seq}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
            <input type="number" step="0.01" data-testid={`item-value-${it.seq}-${k}`}
              value={vals[k] ?? ""}
              onChange={(e) => setVals({ ...vals, [k]: e.target.value })}
              placeholder={it.calc?.[k] != null ? String(it.calc[k]) : "auto"}
              className={inputCls} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button type="button" disabled={saving}
          onClick={() => onSave(Object.fromEntries(fields.map(([k]) => [k, numOrNull(vals[k])])))}
          data-testid={`item-values-save-${it.seq}`}
          className="inline-flex items-center gap-1.5 pill-accent rounded-md px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wider disabled:opacity-60 transition">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salvar valores
        </button>
        <span className="text-[10px] text-slate-600">Vazio = valor calculado. Salvar recalcula o item e o total do orçamento.</span>
      </div>
    </div>
  );
}

// Arquivos vinculados ao item (file_index + item_seq + fotos do jateamento) — nunca mistura itens.
function itemFileIdxs(it, files) {
  const idxs = [];
  if (it.file_index != null) idxs.push(it.file_index);
  (files || []).forEach((f, i) => {
    if (f.item_seq != null && Number(f.item_seq) === Number(it.seq) && !idxs.includes(i)) idxs.push(i);
  });
  ((it.jateamento || {}).photos || []).forEach((p) => { if (!idxs.includes(p)) idxs.push(p); });
  return idxs;
}

// Cabeçalho do item: download do(s) arquivo(s) vinculados + upload admin (ausente ou complementar).
function ItemHeaderDownload({ it, files, onDownload, onAdd }) {
  const [open, setOpen] = useState(false);
  const idxs = itemFileIdxs(it, files);
  const nameOf = (fi) => (files || [])[fi]?.original_filename || `arquivo ${fi + 1}`;
  const addBtn = (label, testid) => (
    <button type="button" data-testid={testid} onClick={() => onAdd(it.seq)}
      className="inline-flex items-center gap-1 border border-dashed border-slate-600 hover:border-orange-500 text-slate-400 hover:text-orange-400 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors">
      <Plus className="w-3 h-3" /> {label}
    </button>
  );
  if (!idxs.length) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid={`item-file-actions-${it.seq}`}>
        <span data-testid={`item-no-file-${it.seq}`} className="text-[10px] tracking-wide text-amber-500/90 normal-case">
          Este item não possui arquivo anexado pelo cliente. Deseja adicionar um arquivo para fabricação?
        </span>
        {addBtn("Adicionar arquivo", `item-add-file-${it.seq}`)}
      </span>
    );
  }
  const dlBtn = idxs.length === 1 ? (
    <button type="button" data-testid={`item-header-download-${it.seq}`}
      onClick={() => onDownload(idxs[0], nameOf(idxs[0]))} title={`Baixar ${nameOf(idxs[0])}`}
      className="inline-flex items-center gap-1 border border-slate-700 hover:border-orange-500 text-slate-300 hover:text-orange-400 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors">
      <FileDown className="w-3 h-3" /> Baixar arquivo
    </button>
  ) : (
    <div className="relative">
      <button type="button" data-testid={`item-header-download-${it.seq}`} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 border border-slate-700 hover:border-orange-500 text-slate-300 hover:text-orange-400 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors">
        <FileDown className="w-3 h-3" /> Baixar arquivo ({idxs.length})
      </button>
      {open && (
        <ul className="absolute left-0 top-full z-30 mt-1 min-w-56 rounded-md border border-slate-700 bg-slate-900 shadow-xl py-1"
          data-testid={`item-header-download-menu-${it.seq}`}>
          {idxs.map((fi) => (
            <li key={fi}>
              <button type="button" data-testid={`item-header-download-${it.seq}-${fi}`}
                onClick={() => { setOpen(false); onDownload(fi, nameOf(fi)); }}
                className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-slate-800 hover:text-orange-400 transition-colors truncate">
                {nameOf(fi)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`item-file-actions-${it.seq}`}>
      {dlBtn}
      {addBtn("Adicionar", `item-add-file-${it.seq}`)}
    </span>
  );
}

function ItemFiles({ quoteId, files, it, onAnalyze, onDownload }) {
  const idxs = itemFileIdxs(it, files);
  if (!idxs.length) return null;
  return (
    <div className="mt-3 border-t border-slate-800/80 pt-3" data-testid={`item-files-${it.seq}`}>
      <h4 className="text-[10px] uppercase tracking-widest text-slate-500">{idxs.length > 1 ? "Peças / Arquivos" : "Peça / Arquivo"}</h4>
      <ul className="mt-2 space-y-2">
        {idxs.map((fi) => {
          const f = (files || [])[fi] || {};
          const name = f.original_filename || `arquivo ${fi + 1}`;
          const ext = name.toLowerCase().split(".").pop();
          const isImg = IMG_EXTS.includes(ext);
          const analyzable = ["dxf", "pdf", "step", "stp"].includes(ext);
          const hasThumb = f.thumbnail_path || f.analysis?.thumbnail_path;
          return (
            <li key={fi} className="flex items-center gap-3 rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2" data-testid={`item-file-${it.seq}-${fi}`}>
              <div className="w-16 h-12 shrink-0 rounded-sm border border-slate-800 bg-slate-950 grid place-content-center overflow-hidden" data-testid={`item-file-preview-${it.seq}-${fi}`}>
                {isImg ? (
                  <Photo quoteId={quoteId} idx={fi} name={name} size="w-full h-full" />
                ) : hasThumb ? (
                  <Thumb quoteId={quoteId} fileIndex={fi} />
                ) : (
                  <div className="text-center">
                    <FileText className="w-4 h-4 text-slate-600 mx-auto" />
                    <span className="text-[8px] uppercase text-slate-600 mono">{ext}</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-300 truncate">{name}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-600 mono" data-testid={`item-file-meta-${it.seq}-${fi}`}>
                  {ext}{f.size != null ? ` · ${f.size >= 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`}` : ""}
                  {f.uploaded_at ? ` · ${fdate(f.uploaded_at)}` : ""}{f.uploaded_by ? ` · ${f.uploaded_by}` : ""}
                </div>
              </div>
              <span data-testid={`item-file-origin-${it.seq}-${fi}`}
                className={`text-[9px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 border shrink-0 ${f.origin === "admin" ? "border-orange-900/60 text-orange-400" : "border-slate-700 text-slate-500"}`}>
                {f.origin === "admin" ? "Administração" : "Cliente"}
              </span>
              {analyzable && !f.analysis && (
                <button type="button" onClick={() => onAnalyze(fi)} data-testid={`item-file-analyze-${it.seq}-${fi}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-orange-500 hover:text-orange-400 shrink-0">
                  <ScanSearch className="w-3.5 h-3.5" /> Analisar
                </button>
              )}
              <button type="button" onClick={() => onDownload(fi, name)} data-testid={`item-file-download-${it.seq}-${fi}`}
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 shrink-0">
                <FileDown className="w-3.5 h-3.5" /> Baixar arquivo
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function QuoteItems({ quoteId, files, items, onChanged, jateamentoRequest }) {
  const [configs, setConfigs] = useState([]);
  const [jatCfg, setJatCfg] = useState(null);
  const [jatAdminCfg, setJatAdminCfg] = useState(null);
  const [dobraPrices, setDobraPrices] = useState([]);
  const [gravPrices, setGravPrices] = useState([]);
  const [usiMaterials, setUsiMaterials] = useState([]);
  const [form, setForm] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sheet-configs").then(({ data }) => setConfigs(data.filter((c) => c.active !== false))).catch(() => {});
    api.get("/jateamento-config/public").then(({ data }) => setJatCfg(data)).catch(() => {});
    api.get("/admin/jateamento-config").then(({ data }) => setJatAdminCfg(data)).catch(() => {});
    api.get("/admin/dobra-prices").then(({ data }) => setDobraPrices((data || []).filter((p) => p.active !== false))).catch(() => {});
    api.get("/admin/gravacao-prices").then(({ data }) => setGravPrices((data || []).filter((p) => p.active !== false))).catch(() => {});
    api.get("/materials").then(({ data }) => setUsiMaterials([...new Set((data || []).filter((m) => m.active !== false).map((m) => `${m.material}${m.grade ? ` ${m.grade}` : ""}`.trim()))].sort())).catch(() => {});
  }, []);

  const dobraMaterials = [...new Set(dobraPrices.map((p) => p.material))].sort();
  const gravMachines = [...new Set(gravPrices.map((p) => p.machine))].sort();
  const gravMaterialsFor = (machine) => [...new Set(gravPrices.filter((p) => p.machine === machine).map((p) => p.material))].sort();
  const gravDepthsFor = (machine, material) => [...new Set(gravPrices.filter((p) => p.machine === machine && p.material === material).map((p) => p.depth))].sort();

  const cfgById = (id) => configs.find((c) => c.id === id);
  const cfgLabel = (c) => [c.material, c.grade, c.thickness_mm != null ? `${c.thickness_mm} mm` : ""].filter(Boolean).join(" · ");

  const imageFiles = (files || [])
    .map((f, i) => ({ f, i }))
    .filter(({ f }) => IMG_EXTS.includes((f.original_filename || "").toLowerCase().split(".").pop()));

  const downloadFile = async (idx, name) => {
    try {
      const { data } = await api.get(`/quote-requests/${quoteId}/files/${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addFileRef = useRef(null);
  const addSeqRef = useRef(null);

  const addItemFile = (seq) => {
    addSeqRef.current = seq;
    addFileRef.current?.click();
  };

  const uploadItemFile = async (file) => {
    const seq = addSeqRef.current;
    if (!seq || !file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/quote-requests/${quoteId}/files?item=${seq}`, fd);
      toast.success(`Arquivo "${file.name}" vinculado ao ITEM ${String(seq).padStart(3, "0")}.`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const analyzeIdx = async (idx) => {
    try {
      await api.post(`/quote-requests/${quoteId}/files/${idx}/analyze`);
      toast.success("Arquivo analisado — métricas e miniatura atualizadas.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveValues = async (it, overrides) => {
    setSaving(true);
    try {
      await api.put(`/quote-requests/${quoteId}/items/${it.seq}`, { overrides });
      toast.success(`ITEM ${String(it.seq).padStart(3, "0")} recalculado — total do orçamento atualizado.`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => setForm({
    item_type: "chapa",
    config_id: "", file_index: "", part_name: "", quantity: 1,
    services: { plasma: false, laser: false, dobra: false }, bend_meters: "", jat_price: "",
    dobra: { ...DOBRA_EMPTY }, dobra_price: "",
    grav: { ...GRAV_EMPTY }, grav_price: "",
    usi: { ...USI_EMPTY }, usi_total: "",
    jat: jateamentoRequest
      ? {
          length_mm: jateamentoRequest.length_mm ?? "", width_mm: jateamentoRequest.width_mm ?? "",
          height_mm: jateamentoRequest.height_mm ?? "", weight_kg: jateamentoRequest.weight_kg ?? "",
          quantity: jateamentoRequest.quantity ?? "1", faces: jateamentoRequest.faces ?? "",
          difficulty: jateamentoRequest.difficulty || "", notes: jateamentoRequest.notes || "",
          piece_type: jateamentoRequest.piece_type || "", part_material: jateamentoRequest.part_material || "",
          part_grade: jateamentoRequest.part_grade || "", part_thickness_mm: jateamentoRequest.part_thickness_mm ?? "",
          characteristics: [...(jateamentoRequest.characteristics || [])],
          characteristics_other: jateamentoRequest.characteristics_other || "",
          photos: [],
        }
      : { ...JAT_EMPTY },
  });

  const jatNum = (v) => (v === "" || v == null ? 0 : Number(v));

  const validateJat = (j) => {
    if (!(jatNum(j.length_mm) > 0 && jatNum(j.width_mm) > 0 && jatNum(j.height_mm) > 0)) return "Informe as dimensões da peça (comprimento, largura e altura em mm).";
    if (!(jatNum(j.weight_kg) > 0)) return "Informe o peso aproximado da peça (kg).";
    if (!(jatNum(j.quantity) >= 1)) return "Informe a quantidade de peças.";
    if (!(jatNum(j.faces) >= 1 && jatNum(j.faces) <= 10)) return "Quantidade de faces deve ser entre 1 e 10.";
    if (!j.difficulty) return "Selecione o nível de dificuldade.";
    return null;
  };

  const jatPayload = (j) => ({
    length_mm: jatNum(j.length_mm), width_mm: jatNum(j.width_mm), height_mm: jatNum(j.height_mm),
    weight_kg: jatNum(j.weight_kg), quantity: jatNum(j.quantity) || 1,
    faces: jatNum(j.faces), difficulty: j.difficulty, notes: j.notes,
    piece_type: j.piece_type || "", part_material: j.part_material || "",
    part_grade: j.part_grade || "", part_thickness_mm: jatNum(j.part_thickness_mm),
    characteristics: j.characteristics || [], characteristics_other: j.characteristics_other || "",
    photos: j.photos,
  });

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.item_type === "jateamento") {
        const errMsg = validateJat(form.jat);
        if (errMsg) { toast.error(errMsg); setSaving(false); return; }
        if (!jatAdminCfg?.price_per_m2 && !(jatNum(form.jat_price) > 0)) {
          toast.error("Informe o valor por m² do jateamento para este item.");
          setSaving(false);
          return;
        }
        await api.post(`/quote-requests/${quoteId}/items`, {
          item_type: "jateamento",
          part_name: form.part_name.trim(),
          quantity: jatNum(form.jat.quantity) || 1,
          jateamento: jatPayload(form.jat),
          overrides: form.jat_price === "" ? undefined : { price_per_m2: jatNum(form.jat_price) },
        });
        toast.success("Item de jateamento calculado.");
      } else if (form.item_type === "usinagem") {
        const u = form.usi;
        if (!u.material) { toast.error("Selecione o material da peça."); setSaving(false); return; }
        if (!u.format) { toast.error("Selecione o formato da matéria-prima."); setSaving(false); return; }
        const dimsOk = u.format === "retangular"
          ? jatNum(u.dim_a) > 0 && jatNum(u.dim_b) > 0 && jatNum(u.dim_c) > 0
          : jatNum(u.dim_a) > 0 && jatNum(u.dim_b) > 0;
        if (!dimsOk) { toast.error("Informe as dimensões da matéria-prima (mm)."); setSaving(false); return; }
        await api.post(`/quote-requests/${quoteId}/items`, {
          item_type: "usinagem",
          part_name: form.part_name.trim(),
          quantity: Math.max(1, jatNum(form.quantity) || 1),
          service_name: u.service_name,
          usinagem: {
            material: u.material, format: u.format,
            dim_a: jatNum(u.dim_a), dim_b: jatNum(u.dim_b),
            dim_c: u.format === "retangular" ? jatNum(u.dim_c) : null,
            tolerance: (u.tolerance || "").trim(),
            quantity: Math.max(1, jatNum(form.quantity) || 1),
          },
          overrides: form.usi_total === "" ? undefined : { total: jatNum(form.usi_total) },
        });
        toast.success("Item de usinagem adicionado.");
      } else if (form.item_type === "gravacao") {
        const g = form.grav;
        if (!g.machine) { toast.error("Selecione a máquina de gravação."); setSaving(false); return; }
        if (!g.material) { toast.error("Selecione o material de gravação."); setSaving(false); return; }
        if (!g.depth) { toast.error("Selecione a profundidade da gravação."); setSaving(false); return; }
        if (!(jatNum(g.width_mm) > 0) || !(jatNum(g.height_mm) > 0)) { toast.error("Informe largura e altura da gravação (mm) maiores que zero."); setSaving(false); return; }
        if (jatNum(g.width_mm) > 100 || jatNum(g.height_mm) > 100) { toast.error("O tamanho máximo permitido para uma gravação é 100 × 100 mm."); setSaving(false); return; }
        await api.post(`/quote-requests/${quoteId}/items`, {
          item_type: "gravacao",
          part_name: form.part_name.trim(),
          quantity: Math.max(1, jatNum(g.quantity) || 1),
          file_index: form.file_index === "" ? null : Number(form.file_index),
          gravacao: {
            machine: g.machine, material: g.material, depth: g.depth,
            width_mm: jatNum(g.width_mm), height_mm: jatNum(g.height_mm),
            quantity: Math.max(1, jatNum(g.quantity) || 1), notes: g.notes || "",
          },
          overrides: form.grav_price === "" ? undefined : { price_per_cm2: jatNum(form.grav_price) },
        });
        toast.success("Item de gravação calculado.");
      } else if (form.item_type === "dobra") {        const db = form.dobra;
        if (!db.material) { toast.error("Selecione o material da dobra."); setSaving(false); return; }
        if (!(jatNum(db.thickness_mm) > 0)) { toast.error("Informe a espessura da chapa (mm)."); setSaving(false); return; }
        if (!(jatNum(db.bend_length_mm) > 0)) { toast.error("Informe o comprimento da dobra (mm)."); setSaving(false); return; }
        if (!(jatNum(db.bend_count) >= 1)) { toast.error("Informe a quantidade de dobras."); setSaving(false); return; }
        await api.post(`/quote-requests/${quoteId}/items`, {
          item_type: "dobra",
          part_name: form.part_name.trim(),
          quantity: Math.max(1, jatNum(form.quantity) || 1),
          dobra: {
            material: db.material, thickness_mm: jatNum(db.thickness_mm),
            bend_length_mm: jatNum(db.bend_length_mm), bend_count: jatNum(db.bend_count) || 1,
            quantity: Math.max(1, jatNum(form.quantity) || 1),
          },
          overrides: form.dobra_price === "" ? undefined : { price_per_meter: jatNum(form.dobra_price) },
        });
        toast.success("Item de dobra calculado.");
      } else {
        const cfg = cfgById(form.config_id);
        if (!cfg) { toast.error("Selecione a configuração de chapa (material/espessura/liga)."); setSaving(false); return; }
        await api.post(`/quote-requests/${quoteId}/items`, {
          file_index: form.file_index === "" ? null : Number(form.file_index),
          part_name: form.part_name.trim(),
          material: cfg.material, thickness_mm: cfg.thickness_mm, grade: cfg.grade || "",
          quantity: Math.max(1, Number(form.quantity) || 1),
          services: form.services,
          metrics: jatNum(form.bend_meters) > 0 ? { bend_meters: jatNum(form.bend_meters) } : null,
        });
        toast.success("Item calculado com as regras de Preços.");
      }
      setForm(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (it) => {
    if ((it.item_type || "chapa") === "usinagem") {
      const u = it.usinagem || {};
      setEdit({
        seq: it.seq, item_type: "usinagem",
        part_name: it.part_name, quantity: it.quantity ?? 1,
        service_name: it.service_name || "Centro de Usinagem CNC",
        usi: {
          material: u.material || it.material || "", format: u.format || "",
          dim_a: u.dim_a ?? "", dim_b: u.dim_b ?? "", dim_c: u.dim_c ?? "",
          tolerance: u.tolerance || "",
        },
        overrides: { total: it.overrides?.total ?? "" },
      });
      return;
    }
    if ((it.item_type || "chapa") === "gravacao") {
      const g = it.gravacao || {};
      setEdit({
        seq: it.seq, item_type: "gravacao",
        part_name: it.part_name,
        grav: {
          machine: g.machine || "", material: g.material || it.material || "",
          depth: g.depth || "", width_mm: g.width_mm ?? "", height_mm: g.height_mm ?? "",
          notes: g.notes || "",
        },
        quantity: it.quantity ?? 1,
        overrides: Object.fromEntries(GRAV_OVERRIDE_FIELDS.map(([k]) => [k, it.overrides?.[k] ?? ""])),
      });
      return;
    }
    if ((it.item_type || "chapa") === "dobra") {
      const db = it.dobra || {};
      setEdit({
        seq: it.seq, item_type: "dobra",
        part_name: it.part_name, quantity: it.quantity ?? 1,
        dobra: {
          material: db.material || it.material || "",
          thickness_mm: db.thickness_mm ?? it.thickness_mm ?? "",
          bend_length_mm: db.bend_length_mm ?? "",
          bend_count: db.bend_count ?? "",
        },
        overrides: Object.fromEntries(DOBRA_OVERRIDE_FIELDS.map(([k]) => [k, it.overrides?.[k] ?? ""])),
      });
      return;
    }
    if ((it.item_type || "chapa") === "jateamento") {
      const j = it.jateamento || {};
      setEdit({
        seq: it.seq, item_type: "jateamento",
        part_name: it.part_name,
        jat: {
          length_mm: j.length_mm ?? "", width_mm: j.width_mm ?? "", height_mm: j.height_mm ?? "",
          weight_kg: j.weight_kg ?? "", quantity: it.quantity ?? 1, faces: j.faces ?? "",
          difficulty: j.difficulty || "", notes: j.notes || "", photos: [...(j.photos || [])],
          piece_type: j.piece_type || "", part_material: j.part_material || "",
          characteristics: [...(j.characteristics || [])], characteristics_other: j.characteristics_other || "",
        },
        overrides: Object.fromEntries(JAT_OVERRIDE_FIELDS.map(([k]) => [k, it.overrides?.[k] ?? ""])),
      });
      return;
    }
    setEdit({
      seq: it.seq, item_type: "chapa",
      part_name: it.part_name, material: it.material, thickness_mm: it.thickness_mm,
      grade: it.grade || "", quantity: it.quantity,
      services: { plasma: false, laser: false, dobra: false, ...(it.services || {}) },
      metrics: Object.fromEntries(METRIC_FIELDS.map(([k]) => [k, it.metrics?.[k] ?? ""])),
      overrides: Object.fromEntries(OVERRIDE_FIELDS.map(([k]) => [k, it.overrides?.[k] ?? ""])),
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const numOrUndef = (v) => (v === "" || v == null ? undefined : Number(v));
      if (edit.item_type === "jateamento") {
        const errMsg = validateJat(edit.jat);
        if (errMsg) { toast.error(errMsg); setSaving(false); return; }
        const overrides = {};
        for (const [k] of JAT_OVERRIDE_FIELDS) {
          const v = numOrUndef(edit.overrides[k]);
          overrides[k] = v === undefined ? null : v;
        }
        await api.put(`/quote-requests/${quoteId}/items/${edit.seq}`, {
          part_name: edit.part_name,
          jateamento: jatPayload(edit.jat),
          overrides,
        });
        toast.success("Item de jateamento recalculado (snapshot de preços preservado).");
      } else if (edit.item_type === "usinagem") {
        const u = edit.usi;
        const dimsOk = u.format === "retangular"
          ? numOrUndef(u.dim_a) > 0 && numOrUndef(u.dim_b) > 0 && numOrUndef(u.dim_c) > 0
          : numOrUndef(u.dim_a) > 0 && numOrUndef(u.dim_b) > 0;
        if (!u.material || !u.format || !dimsOk) {
          toast.error("Preencha material, formato e dimensões da matéria-prima.");
          setSaving(false);
          return;
        }
        const tv = numOrUndef(edit.overrides.total);
        await api.put(`/quote-requests/${quoteId}/items/${edit.seq}`, {
          part_name: edit.part_name,
          service_name: edit.service_name,
          usinagem: {
            material: u.material, format: u.format,
            dim_a: numOrUndef(u.dim_a), dim_b: numOrUndef(u.dim_b),
            dim_c: u.format === "retangular" ? numOrUndef(u.dim_c) : null,
            tolerance: (u.tolerance || "").trim(),
            quantity: Math.max(1, Number(edit.quantity) || 1),
          },
          overrides: { total: tv === undefined ? null : tv },
        });
        toast.success("Item de usinagem atualizado.");
      } else if (edit.item_type === "gravacao") {
        const g = edit.grav;
        if (!g.machine || !g.material || !g.depth
            || !(numOrUndef(g.width_mm) > 0) || !(numOrUndef(g.height_mm) > 0)) {
          toast.error("Preencha máquina, material, profundidade, largura e altura da gravação.");
          setSaving(false);
          return;
        }
        if (numOrUndef(g.width_mm) > 100 || numOrUndef(g.height_mm) > 100) {
          toast.error("O tamanho máximo permitido para uma gravação é 100 × 100 mm.");
          setSaving(false);
          return;
        }
        const overrides = {};
        for (const [k] of GRAV_OVERRIDE_FIELDS) {
          const v = numOrUndef(edit.overrides[k]);
          overrides[k] = v === undefined ? null : v;
        }
        await api.put(`/quote-requests/${quoteId}/items/${edit.seq}`, {
          part_name: edit.part_name,
          gravacao: {
            machine: g.machine, material: g.material, depth: g.depth,
            width_mm: numOrUndef(g.width_mm), height_mm: numOrUndef(g.height_mm),
            quantity: Math.max(1, Number(edit.quantity) || 1), notes: g.notes || "",
          },
          overrides,
        });
        toast.success("Item de gravação recalculado (snapshot de preços preservado).");
      } else if (edit.item_type === "dobra") {
        const db = edit.dobra;
        if (!db.material || !(numOrUndef(db.thickness_mm) > 0)
            || !(numOrUndef(db.bend_length_mm) > 0) || !(numOrUndef(db.bend_count) >= 1)) {
          toast.error("Preencha material, espessura, comprimento da dobra e quantidade de dobras.");
          setSaving(false);
          return;
        }
        const overrides = {};
        for (const [k] of DOBRA_OVERRIDE_FIELDS) {
          const v = numOrUndef(edit.overrides[k]);
          overrides[k] = v === undefined ? null : v;
        }
        await api.put(`/quote-requests/${quoteId}/items/${edit.seq}`, {
          part_name: edit.part_name,
          dobra: {
            material: db.material, thickness_mm: numOrUndef(db.thickness_mm),
            bend_length_mm: numOrUndef(db.bend_length_mm), bend_count: numOrUndef(db.bend_count),
            quantity: Math.max(1, Number(edit.quantity) || 1),
          },
          overrides,
        });
        toast.success("Item de dobra recalculado (snapshot de preços preservado).");
      } else {
        const metrics = {};
        for (const [k] of METRIC_FIELDS) {
          const v = numOrUndef(edit.metrics[k]);
          if (v !== undefined) metrics[k] = v;
        }
        const overrides = {};
        for (const [k] of OVERRIDE_FIELDS) {
          const v = numOrUndef(edit.overrides[k]);
          overrides[k] = v === undefined ? null : v;
        }
        await api.put(`/quote-requests/${quoteId}/items/${edit.seq}`, {
          part_name: edit.part_name, material: edit.material,
          thickness_mm: Number(edit.thickness_mm), grade: edit.grade,
          quantity: Math.max(1, Number(edit.quantity) || 1),
          services: edit.services, metrics, overrides,
        });
        toast.success("Item recalculado (snapshot de preços preservado).");
      }
      setEdit(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (seq) => {
    if (!window.confirm(`Excluir o ITEM ${String(seq).padStart(3, "0")}?`)) return;
    try {
      await api.delete(`/quote-requests/${quoteId}/items/${seq}`);
      toast.success("Item excluído.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const jatExceeded = (jat) => {
    const out = [];
    const b = jatCfg?.booth;
    if (!b) return out;
    for (const [label, val, lim, unit] of [
      ["Comprimento", jatNum(jat.length_mm), b.max_length_mm, "mm"],
      ["Largura", jatNum(jat.width_mm), b.max_width_mm, "mm"],
      ["Altura", jatNum(jat.height_mm), b.max_height_mm, "mm"],
      ["Peso", jatNum(jat.weight_kg), b.max_weight_kg, "kg"],
    ]) {
      if (lim > 0 && val > lim) out.push({ label, val, lim, unit });
    }
    return out;
  };

  const photoPicker = (jat, setJat) => (
    <div className="col-span-2 sm:col-span-4">
      <label className={labelCls}>Fotos da peça (opcional, máx. 5)</label>
      {!imageFiles.length ? (
        <p className="mt-1 text-[11px] text-slate-600">Nenhuma imagem (JPG/PNG) anexada ao orçamento — anexe na seção de arquivos.</p>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {imageFiles.map(({ f, i }) => {
            const on = jat.photos.includes(i);
            const disabled = !on && jat.photos.length >= 5;
            return (
              <button type="button" key={i} data-testid={`jat-photo-toggle-${i}`} disabled={disabled}
                onClick={() => setJat({ ...jat, photos: on ? jat.photos.filter((x) => x !== i) : [...jat.photos, i] })}
                className={`rounded-sm border px-2.5 py-1.5 text-[11px] transition-colors ${on ? "border-orange-500 bg-orange-500/10 text-orange-300" : disabled ? "border-slate-800 text-slate-700 cursor-not-allowed" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                {f.original_filename}
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-1 text-[10px] text-slate-600">{(jat.photos || []).length}/5 foto(s) vinculada(s) — permanecem ligadas a este item do orçamento.</p>
    </div>
  );

  const jatFields = (jat, setJat, prefix) => {
    const exceeded = jatExceeded(jat);
    return (
      <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Comprimento (mm) *</label>
            <input type="number" step="0.01" min="0" data-testid={`${prefix}-length`} value={jat.length_mm} onChange={(e) => setJat({ ...jat, length_mm: e.target.value })} className={inputCls} placeholder="0,00 mm" />
          </div>
          <div>
            <label className={labelCls}>Largura (mm) *</label>
            <input type="number" step="0.01" min="0" data-testid={`${prefix}-width`} value={jat.width_mm} onChange={(e) => setJat({ ...jat, width_mm: e.target.value })} className={inputCls} placeholder="0,00 mm" />
          </div>
          <div>
            <label className={labelCls}>Altura (mm) *</label>
            <input type="number" step="0.01" min="0" data-testid={`${prefix}-height`} value={jat.height_mm} onChange={(e) => setJat({ ...jat, height_mm: e.target.value })} className={inputCls} placeholder="0,00 mm" />
          </div>
          <div>
            <label className={labelCls}>Peso aprox. (kg) *</label>
            <input type="number" step="0.01" min="0" data-testid={`${prefix}-weight`} value={jat.weight_kg} onChange={(e) => setJat({ ...jat, weight_kg: e.target.value })} className={inputCls} placeholder="0,00 kg" />
          </div>
          <div>
            <label className={labelCls}>Quantidade *</label>
            <input type="number" min="1" data-testid={`${prefix}-quantity`} value={jat.quantity} onChange={(e) => setJat({ ...jat, quantity: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Faces (1–10) *</label>
            <select data-testid={`${prefix}-faces`} value={jat.faces} onChange={(e) => setJat({ ...jat, faces: e.target.value })} className={inputCls}>
              <option value="">Selecionar</option>
              {JAT_FACES.map((n) => <option key={n} value={n}>{n} {n === 1 ? "face" : "faces"}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Dificuldade *</label>
            <select data-testid={`${prefix}-difficulty`} value={jat.difficulty} onChange={(e) => setJat({ ...jat, difficulty: e.target.value })} className={inputCls}>
              <option value="">Selecionar</option>
              {(jatCfg?.difficulties || Object.entries(JAT_DIFF_LABELS).map(([key, label]) => ({ key, label }))).map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Tipo de peça</label>
            <input data-testid={`${prefix}-piece-type`} value={jat.piece_type} onChange={(e) => setJat({ ...jat, piece_type: e.target.value })} className={inputCls} placeholder="Ex.: Suporte, chassi" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Material da peça</label>
            <input data-testid={`${prefix}-part-material`} value={jat.part_material} onChange={(e) => setJat({ ...jat, part_material: e.target.value })} className={inputCls} placeholder="Ex.: Aço carbono" />
          </div>
          <div className="col-span-2 sm:col-span-4">
            <label className={labelCls}>O que torna a peça mais difícil de jatear?</label>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5" data-testid={`${prefix}-characteristics`}>
              {JAT_CHARACTERISTICS.map((c) => (
                <label key={c.key} className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
                  <input type="checkbox" data-testid={`${prefix}-char-${c.key}`} checked={(jat.characteristics || []).includes(c.key)}
                    onChange={() => {
                      const cur = jat.characteristics || [];
                      setJat({ ...jat, characteristics: cur.includes(c.key) ? cur.filter((x) => x !== c.key) : [...cur, c.key] });
                    }}
                    className="accent-orange-500 w-3.5 h-3.5" />
                  {c.label}
                </label>
              ))}
            </div>
            {(jat.characteristics || []).includes("outro") && (
              <input data-testid={`${prefix}-char-other`} value={jat.characteristics_other} onChange={(e) => setJat({ ...jat, characteristics_other: e.target.value })} className={`${inputCls} mt-2`} placeholder="Descreva a outra característica…" />
            )}
          </div>
          <div className="col-span-2 sm:col-span-4">
            <label className={labelCls}>Observações</label>
            <textarea rows={2} data-testid={`${prefix}-notes`} value={jat.notes} onChange={(e) => setJat({ ...jat, notes: e.target.value })} className={`${inputCls} resize-none`} placeholder="Geometria irregular, áreas que não devem ser jateadas…" />
          </div>
          {photoPicker(jat, setJat)}
        </div>
        {!!exceeded.length && (
          <div className="mt-3 rounded-sm border border-red-900/60 bg-red-500/10 p-3" data-testid={`${prefix}-capacity-alert`}>
            <div className="text-[11px] font-bold uppercase tracking-widest text-red-400">Peça fora da capacidade da cabine</div>
            <ul className="mt-1.5 space-y-0.5 text-[11px] text-red-300">
              {exceeded.map((x) => (
                <li key={x.label}>{x.label} informado: <b>{x.val} {x.unit}</b> — limite da cabine: <b>{x.lim} {x.unit}</b></li>
              ))}
            </ul>
            <p className="mt-1.5 text-[10px] text-red-300/80">Status técnico: Necessita análise técnica.</p>
          </div>
        )}
      </>
    );
  };

  return (
    <section className="mt-5 card-tactical rounded-md p-6" data-testid="quote-items-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="display text-lg font-bold uppercase tracking-wide">Itens do orçamento ({items?.length || 0})</h2>
        <button data-testid="item-new-button" onClick={() => (form ? setForm(null) : openNew())}
          className="inline-flex items-center gap-1.5 pill-accent rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider transition">
          <Plus className="w-4 h-4" /> Novo item
        </button>
      </div>

      {form && (
        <form onSubmit={create} className="mt-5 rounded-md border border-slate-800 bg-slate-900/40 p-5" data-testid="item-create-form">
          <div className="flex items-center gap-2" data-testid="item-type-toggle">
            <button type="button" data-testid="item-type-chapa" onClick={() => setForm({ ...form, item_type: "chapa" })}
              className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${form.item_type === "chapa" ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
              Chapa (corte/dobra)
            </button>
            <button type="button" data-testid="item-type-jateamento" onClick={() => setForm({ ...form, item_type: "jateamento" })}
              className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${form.item_type === "jateamento" ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
              Jateamento
            </button>
            <button type="button" data-testid="item-type-dobra" onClick={() => setForm({ ...form, item_type: "dobra" })}
              className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${form.item_type === "dobra" ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
              Dobra
            </button>
            <button type="button" data-testid="item-type-gravacao" onClick={() => setForm({ ...form, item_type: "gravacao" })}
              className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${form.item_type === "gravacao" ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
              Gravação
            </button>
            <button type="button" data-testid="item-type-usinagem" onClick={() => setForm({ ...form, item_type: "usinagem" })}
              className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${form.item_type === "usinagem" ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
              Usinagem
            </button>
          </div>

          <div className="mt-4">
            <label className={labelCls}>Nome da peça</label>
            <input data-testid="item-name-input" value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })}
              className={inputCls} placeholder={form.item_type === "jateamento" ? "Ex.: Suporte jateado" : "Se vazio, usa o nome do arquivo DXF"} />
          </div>

          {form.item_type === "jateamento" && (            <div className="mt-4" data-testid="item-jat-fields">
              {jateamentoRequest && (
                <p className="mb-3 text-[11px] text-orange-400/80" data-testid="jat-prefilled-note">
                  Campos preenchidos com os dados informados pelo cliente no wizard — revise antes de calcular.
                </p>
              )}
              {jatFields(form.jat, (j) => setForm({ ...form, jat: j }), "item-jat")}
              <div className="mt-3 max-w-xs">
                <label className={labelCls}>Valor por m² (R$) {jatAdminCfg?.price_per_m2 ? "" : "*"}</label>
                <input type="number" step="0.01" min="0" data-testid="item-jat-price-m2" value={form.jat_price}
                  onChange={(e) => setForm({ ...form, jat_price: e.target.value })} className={inputCls}
                  placeholder={jatAdminCfg?.price_per_m2 ? `Padrão: R$ ${jatAdminCfg.price_per_m2} (vazio usa o padrão)` : "Sem padrão — informe para este item"} />
                <p className="mt-1 text-[10px] text-slate-600">
                  {jatAdminCfg?.price_per_m2
                    ? "Padrão configurado em Materiais/Preços; preencha só para usar um valor diferente neste item."
                    : "Sem valor padrão configurado — o valor informado aqui vale para este item e fica registrado no snapshot."}
                </p>
              </div>
              <p className="mt-3 text-[11px] text-slate-500">
                A área calculada é uma ESTIMATIVA pelas dimensões; para geometrias irregulares, ajuste a área manualmente na edição do item.
              </p>
            </div>
          )}

          {form.item_type === "usinagem" && (
            <div className="mt-4" data-testid="item-usi-fields">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className={labelCls}>Serviço *</label>
                  <select data-testid="item-usi-service" value={form.usi.service_name}
                    onChange={(e) => setForm({ ...form, usi: { ...form.usi, service_name: e.target.value } })} className={inputCls}>
                    {USI_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Material *</label>
                  <select data-testid="item-usi-material" value={form.usi.material}
                    onChange={(e) => setForm({ ...form, usi: { ...form.usi, material: e.target.value } })} className={inputCls}>
                    <option value="">Selecionar</option>
                    {usiMaterials.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Formato da matéria-prima *</label>
                  <select data-testid="item-usi-format" value={form.usi.format}
                    onChange={(e) => setForm({ ...form, usi: { ...form.usi, format: e.target.value, dim_a: "", dim_b: "", dim_c: "" } })} className={inputCls}>
                    <option value="">Selecionar</option>
                    {USI_FMT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Quantidade *</label>
                  <input type="number" min="1" data-testid="item-usi-qty" value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={inputCls} />
                </div>
                {form.usi.format && (
                  <>
                    <div>
                      <label className={labelCls}>{form.usi.format === "cilindrica" ? "Diâmetro (mm) *" : form.usi.format === "quadrada" ? "Lado (mm) *" : "Largura (mm) *"}</label>
                      <input type="number" step="0.01" min="0" data-testid="item-usi-dim-a" value={form.usi.dim_a}
                        onChange={(e) => setForm({ ...form, usi: { ...form.usi, dim_a: e.target.value } })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>{form.usi.format === "retangular" ? "Espessura (mm) *" : "Comprimento (mm) *"}</label>
                      <input type="number" step="0.01" min="0" data-testid="item-usi-dim-b" value={form.usi.dim_b}
                        onChange={(e) => setForm({ ...form, usi: { ...form.usi, dim_b: e.target.value } })} className={inputCls} />
                    </div>
                    {form.usi.format === "retangular" && (
                      <div>
                        <label className={labelCls}>Comprimento (mm) *</label>
                        <input type="number" step="0.01" min="0" data-testid="item-usi-dim-c" value={form.usi.dim_c}
                          onChange={(e) => setForm({ ...form, usi: { ...form.usi, dim_c: e.target.value } })} className={inputCls} />
                      </div>
                    )}
                  </>
                )}
                <div>
                  <label className={labelCls}>Tolerância</label>
                  <input data-testid="item-usi-tolerance" value={form.usi.tolerance}
                    onChange={(e) => setForm({ ...form, usi: { ...form.usi, tolerance: e.target.value } })} className={inputCls} placeholder="Ex.: ± 0,02 mm ou Conforme desenho" />
                </div>
                <div>
                  <label className={labelCls}>Total do item (R$) — manual</label>
                  <input type="number" step="0.01" min="0" data-testid="item-usi-total" value={form.usi_total}
                    onChange={(e) => setForm({ ...form, usi_total: e.target.value })} className={inputCls} placeholder="Vazio = a precificar" />
                </div>
              </div>
            </div>
          )}

          {form.item_type === "gravacao" && (
            <div className="mt-4" data-testid="item-grav-fields">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className={labelCls}>Máquina de gravação *</label>
                  <select data-testid="item-grav-machine" value={form.grav.machine}
                    onChange={(e) => setForm({ ...form, grav: { ...form.grav, machine: e.target.value, material: "", depth: "" } })} className={inputCls}>
                    <option value="">Selecionar</option>
                    {gravMachines.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Material de gravação *</label>
                  <select data-testid="item-grav-material" value={form.grav.material} disabled={!form.grav.machine}
                    onChange={(e) => setForm({ ...form, grav: { ...form.grav, material: e.target.value, depth: "" } })} className={inputCls}>
                    <option value="">{form.grav.machine ? "Selecionar" : "Selecione a máquina"}</option>
                    {gravMaterialsFor(form.grav.machine).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Profundidade *</label>
                  <select data-testid="item-grav-depth" value={form.grav.depth} disabled={!form.grav.material}
                    onChange={(e) => setForm({ ...form, grav: { ...form.grav, depth: e.target.value } })} className={inputCls}>
                    <option value="">{form.grav.material ? "Selecionar" : "Selecione o material"}</option>
                    {gravDepthsFor(form.grav.machine, form.grav.material).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Quantidade *</label>
                  <input type="number" min="1" step="1" data-testid="item-grav-qty" value={form.grav.quantity}
                    onChange={(e) => setForm({ ...form, grav: { ...form.grav, quantity: e.target.value } })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Largura (mm) * — máx. 100</label>
                  <input type="number" step="0.1" min="0" max="100" data-testid="item-grav-width" value={form.grav.width_mm}
                    onChange={(e) => setForm({ ...form, grav: { ...form.grav, width_mm: e.target.value } })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Altura (mm) * — máx. 100</label>
                  <input type="number" step="0.1" min="0" max="100" data-testid="item-grav-height" value={form.grav.height_mm}
                    onChange={(e) => setForm({ ...form, grav: { ...form.grav, height_mm: e.target.value } })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Arquivo/imagem do orçamento</label>
                  <select data-testid="item-grav-file" value={form.file_index} onChange={(e) => setForm({ ...form, file_index: e.target.value })} className={inputCls}>
                    <option value="">Sem vínculo</option>
                    {imageFiles.map(({ f, i }) => <option key={i} value={i}>{f.original_filename}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>R$/cm² manual — opcional</label>
                  <input type="number" step="0.01" min="0" data-testid="item-grav-price" value={form.grav_price}
                    onChange={(e) => setForm({ ...form, grav_price: e.target.value })} className={inputCls} placeholder="Vazio = cadastro da combinação" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Observações</label>
                  <input data-testid="item-grav-notes" value={form.grav.notes}
                    onChange={(e) => setForm({ ...form, grav: { ...form.grav, notes: e.target.value } })} className={inputCls} placeholder="Posição da gravação, referência…" />
                </div>
              </div>
            </div>
          )}

          {form.item_type === "dobra" && (
            <div className="mt-4" data-testid="item-dobra-fields">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Material (Dobra — Materiais/Preços) *</label>
                  <select data-testid="item-dobra-material" value={form.dobra.material}
                    onChange={(e) => setForm({ ...form, dobra: { ...form.dobra, material: e.target.value, thickness_mm: "" } })} className={inputCls}>
                    <option value="">Selecionar material</option>
                    {dobraMaterials.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Espessura (mm) * — digitação livre</label>
                  <input type="number" step="0.01" min="0" data-testid="item-dobra-thickness" value={form.dobra.thickness_mm}
                    onChange={(e) => setForm({ ...form, dobra: { ...form.dobra, thickness_mm: e.target.value } })} className={inputCls} placeholder="Ex.: 1.20" />
                </div>
                <div>
                  <label className={labelCls}>Quantidade *</label>
                  <input type="number" min="1" data-testid="item-dobra-qty" value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Comprimento da dobra (mm) *</label>
                  <input type="number" step="0.01" min="0" data-testid="item-dobra-length" value={form.dobra.bend_length_mm}
                    onChange={(e) => setForm({ ...form, dobra: { ...form.dobra, bend_length_mm: e.target.value } })} className={inputCls} placeholder="Ex.: 470" />
                </div>
                <div>
                  <label className={labelCls}>Quantidade de dobras *</label>
                  <input type="number" min="1" data-testid="item-dobra-count" value={form.dobra.bend_count}
                    onChange={(e) => setForm({ ...form, dobra: { ...form.dobra, bend_count: e.target.value } })} className={inputCls} placeholder="Ex.: 4" />
                </div>
                <div>
                  <label className={labelCls}>R$/m manual — opcional</label>
                  <input type="number" step="0.01" min="0" data-testid="item-dobra-price" value={form.dobra_price}
                    onChange={(e) => setForm({ ...form, dobra_price: e.target.value })} className={inputCls} placeholder="Vazio = cadastro da combinação" />
                </div>
              </div>
              <p className="mt-3 text-[11px] text-slate-500">
                Espessura sem cadastro de preço ou comprimento acima da capacidade da dobradeira → o item é salvo como "Necessita análise técnica" (sem valor automático).
              </p>
            </div>
          )}

          {form.item_type === "chapa" && (
            <>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Chapa (Materiais/Preços) *</label>
                  <select data-testid="item-config-select" value={form.config_id} onChange={(e) => setForm({ ...form, config_id: e.target.value })} className={inputCls}>
                    <option value="">Selecionar material/espessura/liga</option>
                    {configs.map((c) => <option key={c.id} value={c.id}>{cfgLabel(c)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Arquivo DXF (métricas)</label>
                  <select data-testid="item-file-select" value={form.file_index} onChange={(e) => setForm({ ...form, file_index: e.target.value })} className={inputCls}>
                    <option value="">Sem arquivo (inserir manualmente depois)</option>
                    {(files || []).map((f, i) => <option key={i} value={i}>{f.original_filename}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Quantidade</label>
                  <input type="number" min="1" data-testid="item-qty-input" value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-end gap-4">
                {Object.keys(SVC_LABELS).filter((k) => k !== "jateamento").map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input type="checkbox" data-testid={`item-svc-${k}`} checked={!!form.services[k]}
                      onChange={(e) => setForm({ ...form, services: { ...form.services, [k]: e.target.checked } })}
                      className="accent-orange-500 w-4 h-4" />
                    {SVC_LABELS[k]}
                  </label>
                ))}
                <div className="w-44">
                  <label className={labelCls}>Dobra (metros)</label>
                  <input type="number" step="0.01" min="0" data-testid="item-bend-input" value={form.bend_meters}
                    onChange={(e) => setForm({ ...form, bend_meters: e.target.value })} className={inputCls} placeholder="Ex.: 0.6" />
                </div>
              </div>
            </>
          )}

          <div className="mt-4 flex gap-3">
            <button data-testid="item-create-button" disabled={saving}
              className="inline-flex items-center gap-2 pill-accent rounded-md px-5 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Calcular item
            </button>
            <button type="button" onClick={() => setForm(null)}
              className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-300">Cancelar</button>
          </div>
        </form>
      )}

      <input ref={addFileRef} type="file" className="hidden" data-testid="item-add-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadItemFile(f); e.target.value = ""; }} />
      <div className="mt-4 space-y-3" data-testid="quote-items-list">
        {(items || []).map((it) => {
          const isJat = (it.item_type || "chapa") === "jateamento";
          const isDobra = (it.item_type || "chapa") === "dobra";
          const isGrav = (it.item_type || "chapa") === "gravacao";
          const isUsi = (it.item_type || "chapa") === "usinagem";
          const calc = it.calc || {};
          const snap = calc.snapshot || {};
          const jat = it.jateamento || {};
          const dob = it.dobra || {};
          const grav = it.gravacao || {};
          const usi = it.usinagem || {};
          return (
            <div key={it.seq} className="rounded-md border border-slate-800 bg-slate-900/50 p-4" data-testid={`quote-item-${it.seq}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-60">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="mono text-xs text-orange-500 font-bold">ITEM {String(it.seq).padStart(3, "0")}</span>
                    <span className="text-sm font-semibold text-slate-200">{it.part_name}</span>
                    <ItemHeaderDownload it={it} files={files} onDownload={downloadFile} onAdd={addItemFile} />
                    {isJat ? (
                      <span className="text-[10px] uppercase tracking-widest border border-orange-900/60 bg-orange-500/10 text-orange-300 rounded-sm px-1.5 py-0.5">Jateamento</span>
                    ) : isDobra ? (
                      <span className="text-[10px] uppercase tracking-widest border border-orange-900/60 bg-orange-500/10 text-orange-300 rounded-sm px-1.5 py-0.5">Dobra</span>
                    ) : isGrav ? (
                      <span className="text-[10px] uppercase tracking-widest border border-orange-900/60 bg-orange-500/10 text-orange-300 rounded-sm px-1.5 py-0.5">Gravação</span>
                    ) : isUsi ? (
                      <span className="text-[10px] uppercase tracking-widest border border-orange-900/60 bg-orange-500/10 text-orange-300 rounded-sm px-1.5 py-0.5">{it.service_name || "Usinagem"}</span>
                    ) : (
                      Object.entries(it.services || {}).filter(([, on]) => on).map(([k]) => (
                        <span key={k} className="text-[10px] uppercase tracking-widest border border-slate-700 text-slate-400 rounded-sm px-1.5 py-0.5">{SVC_LABELS[k] || k}</span>
                      ))
                    )}
                    {(isJat || isDobra || isGrav || isUsi) && it.tech_status && (
                      <span data-testid={`${isJat ? "jat" : isDobra ? "dobra" : isGrav ? "grav" : "usi"}-tech-status-${it.seq}`}
                        className={`text-[9px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${it.tech_status === "Apto para cálculo automático" ? "text-emerald-400 border-emerald-900/60" : "text-amber-400 border-amber-900/60"}`}>
                        {it.tech_status}
                      </span>
                    )}
                  </div>
                  {isJat ? (
                    <div className="mt-1 text-xs text-slate-500">
                      {jat.piece_type ? `${jat.piece_type} · ` : ""}{jat.part_material ? `${jat.part_material}${jat.part_grade ? ` ${jat.part_grade}` : ""}${jat.part_thickness_mm ? ` ${jat.part_thickness_mm} mm` : ""} · ` : ""}
                      {jat.length_mm} × {jat.width_mm} × {jat.height_mm} mm
                      {" · "}{jat.weight_kg} kg
                      {" · "}{jat.faces} {Number(jat.faces) === 1 ? "face" : "faces"}
                      {" · "}Dificuldade {JAT_DIFF_LABELS[jat.difficulty] || jat.difficulty}
                      {" · "}{it.quantity} un.
                      {(jat.photos || []).length ? ` · ${jat.photos.length} foto(s)` : ""}
                      {!!(jat.characteristics || []).length && (
                        <span className="block mt-0.5 text-slate-600" data-testid={`jat-characteristics-${it.seq}`}>
                          Dificuldades: {jat.characteristics.map(jatCharLabel).join(", ")}{jat.characteristics_other ? ` — ${jat.characteristics_other}` : ""}
                        </span>
                      )}
                    </div>
                  ) : isDobra ? (
                    <div className="mt-1 text-xs text-slate-500">
                      {dob.material || it.material}{dob.thickness_mm != null ? ` ${dob.thickness_mm} mm` : ""}
                      {" · "}dobra {dob.bend_length_mm} mm × {dob.bend_count}
                      {" · "}{it.quantity} un.
                    </div>
                  ) : isGrav ? (
                    <div className="mt-1 text-xs text-slate-500">
                      {grav.machine}{grav.material ? ` · ${grav.material}` : ""}
                      {grav.width_mm != null ? ` · ${grav.width_mm}×${grav.height_mm} mm` : ""}
                      {grav.depth ? ` · ${grav.depth}` : ""}
                      {" · "}{it.quantity} un.
                    </div>
                  ) : isUsi ? (
                    <div className="mt-1 text-xs text-slate-500">
                      {usi.material || it.material}
                      {usi.format ? ` · ${USI_FMT.find(([v]) => v === usi.format)?.[1] || usi.format}` : ""}
                      {usi.format ? ` · ${usiDimsText(usi)}` : ""}
                      {usi.tolerance ? ` · Tol. ${usi.tolerance}` : ""}
                      {" · "}{it.quantity} un.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {[it.material, it.thickness_mm != null ? `${it.thickness_mm} mm` : "", it.grade].filter(Boolean).join(" · ")}
                      {" · "}{it.quantity} un.
                    </div>
                  )}

                  <h4 className="mt-3 text-[10px] uppercase tracking-widest text-slate-500" data-testid={`item-scope-${it.seq}`}>Escopo do cálculo</h4>
                  {isJat ? (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid={`item-breakdown-${it.seq}`}>
                      {JAT_CALC_ROWS.filter(([k]) => calc[k] != null || k === "minimum_applied").map(([k, label, fmt]) => (
                        <div key={k} className="rounded-sm border border-slate-800/80 bg-slate-950/60 px-2 py-1">
                          <div className="text-slate-600 uppercase tracking-wider text-[9px]">{label}</div>
                          <div className="text-slate-300 mono text-[11px]">{calc[k] == null ? "—" : fmt(calc[k])}</div>
                        </div>
                      ))}
                    </div>
                  ) : isDobra ? (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={`item-breakdown-${it.seq}`}>
                      {DOBRA_CALC_ROWS.filter(([k]) => calc[k] != null || k === "minimum_applied").map(([k, label, fmt]) => (
                        <div key={k} className="rounded-sm border border-slate-800/80 bg-slate-950/60 px-2 py-1">
                          <div className="text-slate-600 uppercase tracking-wider text-[9px]">{label}</div>
                          <div className="text-slate-300 mono text-[11px]">{calc[k] == null ? "—" : fmt(calc[k])}</div>
                        </div>
                      ))}
                    </div>
                  ) : isGrav ? (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={`item-breakdown-${it.seq}`}>
                      {GRAV_CALC_ROWS.filter(([k]) => calc[k] != null || k === "minimum_applied").map(([k, label, fmt]) => (
                        <div key={k} className="rounded-sm border border-slate-800/80 bg-slate-950/60 px-2 py-1">
                          <div className="text-slate-600 uppercase tracking-wider text-[9px]">{label}</div>
                          <div className="text-slate-300 mono text-[11px]">{calc[k] == null ? "—" : fmt(calc[k])}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={`item-breakdown-${it.seq}`}>
                      {CALC_ROWS.filter(([k]) => calc[k] != null).map(([k, label, fmt]) => (
                        <div key={k} className="rounded-sm border border-slate-800/80 bg-slate-950/60 px-2 py-1">
                          <div className="text-slate-600 uppercase tracking-wider text-[9px]">{label}</div>
                          <div className="text-slate-300 mono text-[11px]">{fmt(calc[k])}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!isJat && !isDobra && !isGrav && calc.composition && (
                    <p className="mt-1.5 text-[10px] text-slate-500 mono leading-relaxed" data-testid={`item-composition-${it.seq}`}>
                      {compositionText(calc)}
                    </p>
                  )}

                  {isJat && !!calc.capacity_exceeded?.length && (
                    <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-400" data-testid={`jat-capacity-note-${it.seq}`}>
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        Fora da capacidade da cabine: {calc.capacity_exceeded.map((x) => `${x.label} ${x.informed} ${x.unit} (limite ${x.limit} ${x.unit})`).join("; ")}.
                      </span>
                    </div>
                  )}
                  {(isJat || isDobra || isGrav || isUsi) && !!calc.tech_notes?.length && (
                    <p className="mt-1 text-[10px] text-slate-500">{calc.tech_notes.join(" ")}</p>
                  )}
                  {isJat && calc.estimate_note && (
                    <p className="mt-1 text-[10px] text-slate-600 italic">{calc.estimate_note}</p>
                  )}

                  <div className="mt-3 border-t border-slate-800/80 pt-3">
                    <h4 className="text-[10px] uppercase tracking-widest text-slate-500">Valores</h4>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="text-xs text-slate-500">
                        Unitário: <span className="mono text-slate-300" data-testid={`item-unit-${it.seq}`}>{calc.total == null ? "sob análise" : brl(it.unit_price)}</span>
                      </span>
                      <span className="text-xs text-slate-500">
                        Total: <span className="mono text-orange-400 font-semibold" data-testid={`item-total-${it.seq}`}>{calc.total == null ? "Sob análise técnica" : brl(it.total)}</span>
                      </span>
                    </div>
                    <ItemValues key={`${it.seq}-${it.total}-${it.unit_price}`} it={it} fields={valueFieldsFor(it)}
                      saving={saving} onSave={(ov) => saveValues(it, ov)} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {snap.gravacao_price && (
                      <span className="text-[10px] text-slate-600" data-testid={`item-snapshot-${it.seq}`}>
                        snapshot: R$/cm² {snap.gravacao_price.price_per_cm2 ?? "—"} · mín. {snap.gravacao_price.minimum_value ?? "—"}{" "}
                        · {fdate(snap.calculated_at)}{snap.calculated_by ? ` · ${snap.calculated_by}` : ""}{snap.edited_at ? " · editado" : ""}
                        {snap.manual_adjustment ? " · ajuste manual" : ""}
                      </span>
                    )}
                    {snap.dobra_price && (
                      <span className="text-[10px] text-slate-600" data-testid={`item-snapshot-${it.seq}`}>
                        snapshot: R$/m {snap.dobra_price.price_per_meter ?? "—"} · mín. {snap.dobra_price.minimum_value ?? "—"}{" "}
                        · {fdate(snap.calculated_at)}{snap.calculated_by ? ` · ${snap.calculated_by}` : ""}{snap.edited_at ? " · editado" : ""}
                        {snap.manual_adjustment ? " · ajuste manual" : ""}
                      </span>
                    )}
                    {snap.config && (
                      <span className="text-[10px] text-slate-600" data-testid={`item-snapshot-${it.seq}`}>
                        snapshot: {snap.config.material} {snap.config.thickness_mm}mm{snap.config.grade ? ` ${snap.config.grade}` : ""} · R$/m² {snap.config.price_per_m2}{" "}
                        · {fdate(snap.calculated_at)}{snap.calculated_by ? ` · ${snap.calculated_by}` : ""}{snap.edited_at ? " · editado" : ""}
                      </span>
                    )}
                    {snap.jateamento_config && (
                      <span className="text-[10px] text-slate-600" data-testid={`item-snapshot-${it.seq}`}>
                        snapshot: R$/m² {snap.jateamento_config.price_per_m2 ?? "—"} · mín. {snap.jateamento_config.minimum_value ?? "—"}{" "}
                        · {fdate(snap.calculated_at)}{snap.calculated_by ? ` · ${snap.calculated_by}` : ""}{snap.edited_at ? " · editado" : ""}
                        {snap.manual_adjustment ? " · ajuste manual" : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2 shrink-0">
                  <button onClick={() => openEdit(it)} data-testid={`item-edit-${it.seq}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button onClick={() => remove(it.seq)} data-testid={`item-delete-${it.seq}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Excluir
                  </button>
                </div>
              </div>
              <ItemFiles quoteId={quoteId} files={files} it={it} onAnalyze={analyzeIdx} onDownload={downloadFile} />
            </div>
          );
        })}
        {!items?.length && !form && (
          <p className="text-sm text-slate-500" data-testid="quote-items-empty">
            Nenhum item calculado ainda — adicione itens (chapa ou jateamento) para montar o orçamento.
          </p>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70" onClick={() => setEdit(null)}>
          <form onSubmit={saveEdit} role="dialog" aria-modal="true" className="card-tactical rounded-md p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="item-edit-form">
            <div className="flex items-center justify-between">
              <h3 className="display text-base font-bold uppercase tracking-wide">
                Editar ITEM {String(edit.seq).padStart(3, "0")}{edit.item_type === "jateamento" ? " — Jateamento" : edit.item_type === "dobra" ? " — Dobra de Chapas" : edit.item_type === "gravacao" ? " — Gravação a Laser" : edit.item_type === "usinagem" ? " — Usinagem" : ""}
              </h3>
              <button type="button" onClick={() => setEdit(null)}><X className="w-4 h-4 text-slate-500 hover:text-slate-300" /></button>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Nome da peça</label>
              <input data-testid="item-edit-name" value={edit.part_name} onChange={(e) => setEdit({ ...edit, part_name: e.target.value })} className={inputCls} />
            </div>

            {edit.item_type === "usinagem" && (
              <>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="item-edit-usi-fields">
                  <div>
                    <label className={labelCls}>Serviço</label>
                    <select data-testid="item-edit-usi-service" value={edit.service_name}
                      onChange={(e) => setEdit({ ...edit, service_name: e.target.value })} className={inputCls}>
                      {USI_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Material</label>
                    <select data-testid="item-edit-usi-material" value={edit.usi.material}
                      onChange={(e) => setEdit({ ...edit, usi: { ...edit.usi, material: e.target.value } })} className={inputCls}>
                      <option value="">Selecionar</option>
                      {[...new Set([...usiMaterials, edit.usi.material].filter(Boolean))].sort().map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Formato</label>
                    <select data-testid="item-edit-usi-format" value={edit.usi.format}
                      onChange={(e) => setEdit({ ...edit, usi: { ...edit.usi, format: e.target.value, dim_a: "", dim_b: "", dim_c: "" } })} className={inputCls}>
                      <option value="">Selecionar</option>
                      {USI_FMT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Quantidade</label>
                    <input type="number" min="1" data-testid="item-edit-usi-qty" value={edit.quantity}
                      onChange={(e) => setEdit({ ...edit, quantity: e.target.value })} className={inputCls} />
                  </div>
                  {edit.usi.format && (
                    <>
                      <div>
                        <label className={labelCls}>{edit.usi.format === "cilindrica" ? "Diâmetro (mm)" : edit.usi.format === "quadrada" ? "Lado (mm)" : "Largura (mm)"}</label>
                        <input type="number" step="0.01" min="0" data-testid="item-edit-usi-dim-a" value={edit.usi.dim_a}
                          onChange={(e) => setEdit({ ...edit, usi: { ...edit.usi, dim_a: e.target.value } })} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>{edit.usi.format === "retangular" ? "Espessura (mm)" : "Comprimento (mm)"}</label>
                        <input type="number" step="0.01" min="0" data-testid="item-edit-usi-dim-b" value={edit.usi.dim_b}
                          onChange={(e) => setEdit({ ...edit, usi: { ...edit.usi, dim_b: e.target.value } })} className={inputCls} />
                      </div>
                      {edit.usi.format === "retangular" && (
                        <div>
                          <label className={labelCls}>Comprimento (mm)</label>
                          <input type="number" step="0.01" min="0" data-testid="item-edit-usi-dim-c" value={edit.usi.dim_c}
                            onChange={(e) => setEdit({ ...edit, usi: { ...edit.usi, dim_c: e.target.value } })} className={inputCls} />
                        </div>
                      )}
                    </>
                  )}
                  <div>
                    <label className={labelCls}>Tolerância</label>
                    <input data-testid="item-edit-usi-tolerance" value={edit.usi.tolerance}
                      onChange={(e) => setEdit({ ...edit, usi: { ...edit.usi, tolerance: e.target.value } })} className={inputCls} placeholder="Ex.: ± 0,02 mm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Total do item forçado (R$)</label>
                    <input type="number" step="0.01" data-testid="item-edit-override-total" value={edit.overrides.total}
                      onChange={(e) => setEdit({ ...edit, overrides: { ...edit.overrides, total: e.target.value } })} className={inputCls} placeholder="Vazio = a precificar" />
                  </div>
                </div>
              </>
            )}

            {edit.item_type === "gravacao" && (
              <>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="item-edit-grav-fields">
                  <div>
                    <label className={labelCls}>Máquina</label>
                    <select data-testid="item-edit-grav-machine" value={edit.grav.machine}
                      onChange={(e) => setEdit({ ...edit, grav: { ...edit.grav, machine: e.target.value, material: "", depth: "" } })} className={inputCls}>
                      <option value="">Selecionar</option>
                      {[...new Set([...gravMachines, edit.grav.machine].filter(Boolean))].sort().map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Material</label>
                    <select data-testid="item-edit-grav-material" value={edit.grav.material}
                      onChange={(e) => setEdit({ ...edit, grav: { ...edit.grav, material: e.target.value, depth: "" } })} className={inputCls}>
                      <option value="">Selecionar</option>
                      {[...new Set([...gravMaterialsFor(edit.grav.machine), edit.grav.material].filter(Boolean))].sort().map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Profundidade</label>
                    <select data-testid="item-edit-grav-depth" value={edit.grav.depth}
                      onChange={(e) => setEdit({ ...edit, grav: { ...edit.grav, depth: e.target.value } })} className={inputCls}>
                      <option value="">Selecionar</option>
                      {[...new Set([...gravDepthsFor(edit.grav.machine, edit.grav.material), edit.grav.depth].filter(Boolean))].sort().map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Quantidade</label>
                    <input type="number" min="1" step="1" data-testid="item-edit-grav-qty" value={edit.quantity}
                      onChange={(e) => setEdit({ ...edit, quantity: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Largura (mm) — máx. 100</label>
                    <input type="number" step="0.1" min="0" max="100" data-testid="item-edit-grav-width" value={edit.grav.width_mm}
                      onChange={(e) => setEdit({ ...edit, grav: { ...edit.grav, width_mm: e.target.value } })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Altura (mm) — máx. 100</label>
                    <input type="number" step="0.1" min="0" max="100" data-testid="item-edit-grav-height" value={edit.grav.height_mm}
                      onChange={(e) => setEdit({ ...edit, grav: { ...edit.grav, height_mm: e.target.value } })} className={inputCls} />
                  </div>
                </div>
                <h4 className="mt-5 text-xs uppercase tracking-wider text-slate-500">
                  Ajustes do admin (vazio = snapshot/cadastro) — alterar recalcula e registra ajuste manual
                </h4>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {GRAV_OVERRIDE_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
                      <input type="number" step="0.01" data-testid={`item-edit-override-${k}`} value={edit.overrides[k]}
                        onChange={(e) => setEdit({ ...edit, overrides: { ...edit.overrides, [k]: e.target.value } })} className={inputCls} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {edit.item_type === "dobra" && (
              <>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="item-edit-dobra-fields">
                  <div className="col-span-2">
                    <label className={labelCls}>Material</label>
                    <select data-testid="item-edit-dobra-material" value={edit.dobra.material}
                      onChange={(e) => setEdit({ ...edit, dobra: { ...edit.dobra, material: e.target.value } })} className={inputCls}>
                      <option value="">Selecionar material</option>
                      {[...new Set([...dobraMaterials, edit.dobra.material].filter(Boolean))].sort().map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Espessura (mm)</label>
                    <input type="number" step="0.01" min="0" data-testid="item-edit-dobra-thickness" value={edit.dobra.thickness_mm}
                      onChange={(e) => setEdit({ ...edit, dobra: { ...edit.dobra, thickness_mm: e.target.value } })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Quantidade</label>
                    <input type="number" min="1" data-testid="item-edit-dobra-qty" value={edit.quantity}
                      onChange={(e) => setEdit({ ...edit, quantity: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Comprimento da dobra (mm)</label>
                    <input type="number" step="0.01" min="0" data-testid="item-edit-dobra-length" value={edit.dobra.bend_length_mm}
                      onChange={(e) => setEdit({ ...edit, dobra: { ...edit.dobra, bend_length_mm: e.target.value } })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Quantidade de dobras</label>
                    <input type="number" min="1" data-testid="item-edit-dobra-count" value={edit.dobra.bend_count}
                      onChange={(e) => setEdit({ ...edit, dobra: { ...edit.dobra, bend_count: e.target.value } })} className={inputCls} />
                  </div>
                </div>
                <h4 className="mt-5 text-xs uppercase tracking-wider text-slate-500">
                  Ajustes do admin (vazio = snapshot/cadastro) — alterar recalcula e registra ajuste manual
                </h4>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {DOBRA_OVERRIDE_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
                      <input type="number" step="0.01" data-testid={`item-edit-override-${k}`} value={edit.overrides[k]}
                        onChange={(e) => setEdit({ ...edit, overrides: { ...edit.overrides, [k]: e.target.value } })} className={inputCls} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {edit.item_type === "jateamento" && (
              <>
                <div className="mt-4">{jatFields(edit.jat, (j) => setEdit({ ...edit, jat: j }), "item-edit-jat")}</div>
                <h4 className="mt-5 text-xs uppercase tracking-wider text-slate-500">
                  Ajustes do admin (vazio = configuração atual) — alterar recalcula e registra ajuste manual
                </h4>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {JAT_OVERRIDE_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
                      <input type="number" step="0.0001" data-testid={`item-edit-override-${k}`} value={edit.overrides[k]}
                        onChange={(e) => setEdit({ ...edit, overrides: { ...edit.overrides, [k]: e.target.value } })} className={inputCls} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {edit.item_type === "chapa" && (
              <>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className={labelCls}>Material</label>
                    <input data-testid="item-edit-material" value={edit.material} onChange={(e) => setEdit({ ...edit, material: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Espessura (mm)</label>
                    <input type="number" step="0.1" data-testid="item-edit-thickness" value={edit.thickness_mm} onChange={(e) => setEdit({ ...edit, thickness_mm: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Liga</label>
                    <input data-testid="item-edit-grade" value={edit.grade} onChange={(e) => setEdit({ ...edit, grade: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Quantidade</label>
                    <input type="number" min="1" data-testid="item-edit-qty" value={edit.quantity} onChange={(e) => setEdit({ ...edit, quantity: e.target.value })} className={inputCls} />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-4">
                  {Object.keys(SVC_LABELS).filter((k) => k !== "jateamento").map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                      <input type="checkbox" checked={!!edit.services[k]}
                        onChange={(e) => setEdit({ ...edit, services: { ...edit.services, [k]: e.target.checked } })}
                        className="accent-orange-500 w-4 h-4" />
                      {SVC_LABELS[k]}
                    </label>
                  ))}
                </div>
                <h4 className="mt-5 text-xs uppercase tracking-wider text-slate-500">Métricas manuais (sem DXF)</h4>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {METRIC_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
                      <input type="number" step="0.0001" data-testid={`item-edit-metric-${k}`} value={edit.metrics[k]}
                        onChange={(e) => setEdit({ ...edit, metrics: { ...edit.metrics, [k]: e.target.value } })} className={inputCls} />
                    </div>
                  ))}
                </div>
                <h4 className="mt-5 text-xs uppercase tracking-wider text-slate-500">Overrides do admin (vazio = snapshot)</h4>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {OVERRIDE_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
                      <input type="number" step="0.01" data-testid={`item-edit-override-${k}`} value={edit.overrides[k]}
                        onChange={(e) => setEdit({ ...edit, overrides: { ...edit.overrides, [k]: e.target.value } })} className={inputCls} />
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="mt-5 flex gap-3 sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-slate-900/95 backdrop-blur border-t border-slate-800 rounded-b-md">
              <button data-testid="item-edit-save" disabled={saving}
                className="inline-flex items-center gap-2 pill-accent rounded-md px-5 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar e recalcular
              </button>
              <button type="button" onClick={() => setEdit(null)}
                className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-300">Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
